import {
  Bot,
  CheckCircle2,
  GitBranch,
  LockKeyhole,
  LoaderCircle,
  Mail,
  MailCheck,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Star,
  Target,
  User,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import {
  getGithubOAuthStatus,
  login,
  register,
  sendVerificationEmail,
  startGithubOAuth,
} from '../api.js'
import { useAuth } from '../auth-context.js'
import TurnstileWidget from '../components/TurnstileWidget.jsx'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import { saveGithubOAuthState } from '../github-oauth.js'
import { NOINDEX_ROBOTS, setPageMetadata } from '../page-metadata.js'

const productFeatures = [
  { icon: <Target size={18} />, titleKey: 'authMarketing.feature1Title', descriptionKey: 'authMarketing.feature1Desc' },
  { icon: <Sparkles size={18} />, titleKey: 'authMarketing.feature2Title', descriptionKey: 'authMarketing.feature2Desc' },
  { icon: <Star size={18} />, titleKey: 'authMarketing.feature3Title', descriptionKey: 'authMarketing.feature3Desc' },
  { icon: <Bot size={18} />, titleKey: 'authMarketing.feature4Title', descriptionKey: 'authMarketing.feature4Desc' },
]

const TURNSTILE_TOKEN_ERROR_CODES = new Set([
  'TURNSTILE_TOKEN_REQUIRED',
  'TURNSTILE_VERIFICATION_FAILED',
])

function getAuthErrorMessage(error, t) {
  if (TURNSTILE_TOKEN_ERROR_CODES.has(error?.code)) {
    return t('auth.turnstileVerificationFailed')
  }
  if (error?.code === 'TURNSTILE_UNAVAILABLE') return t('auth.turnstileUnavailable')
  if (error?.code === 'TURNSTILE_NOT_CONFIGURED') return t('auth.turnstileMissing')
  if (error?.code === 'EMAIL_ALREADY_EXISTS') return t('auth.emailAlreadyExists')
  if (error?.code === 'USERNAME_ALREADY_EXISTS') return t('auth.usernameAlreadyExists')
  return error?.message || t('auth.requestFailed')
}

function createLoginChallenge() {
  const first = Math.floor(Math.random() * 7) + 3
  const second = Math.floor(Math.random() * 6) + 2
  return {
    label: `${first} + ${second}`,
    answer: String(first + second),
  }
}

function AuthProductPanel() {
  const { t } = useLanguage()
  return (
    <aside className="auth-product-panel">
      <Link to="/" className="auth-brand auth-product-brand">
        <span className="brand-icon"><GitBranch size={23} /></span>
        <span>
          <strong>{t('nav.brand')}</strong>
          <small>{t('nav.brandSub')}</small>
        </span>
      </Link>

      <div className="auth-product-copy">
        <span className="eyebrow">{t('homeGrowth.eyebrow')}</span>
        <h1>{t('authMarketing.title')}</h1>
        <p>{t('authMarketing.description')}</p>
      </div>

      <div className="auth-feature-grid" aria-label={t('authMarketing.featuresAria')}>
        {productFeatures.map((feature) => (
          <article key={feature.titleKey}>
            <span>{feature.icon}</span>
            <div>
              <h2>{t(feature.titleKey)}</h2>
              <p>{t(feature.descriptionKey)}</p>
            </div>
          </article>
        ))}
      </div>

      <div className="auth-preview-card" aria-label={t('authMarketing.previewTitle')}>
        <div className="auth-preview-header">
          <span>{t('authMarketing.previewLabel')}</span>
          <strong>{t('authMarketing.previewTag')}</strong>
        </div>
        <div className="auth-preview-body">
          <h2>{t('authMarketing.previewTitle')}</h2>
          <p>{t('authMarketing.previewDesc')}</p>
          <div className="auth-preview-badges">
            <span>{t('authMarketing.badge1')}</span>
            <span>{t('authMarketing.badge2')}</span>
            <span>{t('authMarketing.badge3')}</span>
          </div>
        </div>
      </div>
    </aside>
  )
}

function AuthPage({ mode }) {
  const isRegister = mode === 'register'
  const { t, language } = useLanguage()
  const turnstileSiteKey = String(import.meta.env.VITE_TURNSTILE_SITE_KEY || '').trim()
  const { user, completeLogin } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [form, setForm] = useState({ username: '', email: '', password: '', confirmPassword: '' })
  const [turnstileToken, setTurnstileToken] = useState('')
  const [turnstileResetKey, setTurnstileResetKey] = useState(0)
  const [turnstileRequested, setTurnstileRequested] = useState(false)
  const pendingRegistrationRef = useRef(false)
  const turnstileServerRetryCountRef = useRef(0)
  const [githubOAuthEnabled, setGithubOAuthEnabled] = useState(null)
  const [githubSubmitting, setGithubSubmitting] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [resending, setResending] = useState(false)
  const [registrationResult, setRegistrationResult] = useState(null)
  const [unverifiedEmail, setUnverifiedEmail] = useState('')
  const [resendMessage, setResendMessage] = useState('')
  const [error, setError] = useState('')
  const [loginChallenge, setLoginChallenge] = useState(() => createLoginChallenge())
  const [loginChallengeAnswer, setLoginChallengeAnswer] = useState('')

  useEffect(() => setPageMetadata({
    title: isRegister ? t('authMarketing.registerTitle') : t('authMarketing.loginTitle'),
    description: isRegister ? t('authMarketing.registerDescription') : t('authMarketing.loginDescription'),
    robots: NOINDEX_ROBOTS,
  }), [isRegister, language, t])

  useEffect(() => {
    setError('')
    setRegistrationResult(null)
    setUnverifiedEmail('')
    setResendMessage('')
    setTurnstileToken('')
    setTurnstileRequested(false)
    setTurnstileResetKey((current) => current + 1)
    pendingRegistrationRef.current = false
    turnstileServerRetryCountRef.current = 0
    setLoginChallenge(createLoginChallenge())
    setLoginChallengeAnswer('')
  }, [mode])

  useEffect(() => {
    if (isRegister) return undefined
    const controller = new AbortController()
    setGithubOAuthEnabled(null)
    getGithubOAuthStatus(controller.signal)
      .then(({ enabled }) => setGithubOAuthEnabled(enabled === true))
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setGithubOAuthEnabled(null)
      })
    return () => controller.abort()
  }, [isRegister])

  if (user) return <Navigate to="/" replace />

  const updateField = (event) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }))
  }

  const loginChallengePassed = loginChallengeAnswer.trim() === loginChallenge.answer

  const resetLoginChallenge = () => {
    setLoginChallenge(createLoginChallenge())
    setLoginChallengeAnswer('')
  }

  const submitRegistration = async (verificationToken) => {
    setSubmitting(true)
    try {
      const data = await register({
        username: form.username.trim(),
        email: form.email.trim(),
        password: form.password,
        turnstileToken: verificationToken,
      })
      pendingRegistrationRef.current = false
      navigate('/login', {
        replace: true,
        state: {
          registrationEmail: data.email || form.email.trim(),
          verificationMailSent: true,
        },
      })
    } catch (requestError) {
      const tokenRejected = TURNSTILE_TOKEN_ERROR_CODES.has(requestError.code)
      const accountCreated = requestError.details?.accountCreated
      setError(getAuthErrorMessage(requestError, t))
      if (accountCreated) {
        pendingRegistrationRef.current = false
        setRegistrationResult({ email: requestError.details.email || form.email.trim(), mailSent: false })
      }
      if (turnstileSiteKey) {
        setTurnstileToken('')
        setTurnstileResetKey((current) => current + 1)
        if (tokenRejected && !accountCreated && turnstileServerRetryCountRef.current < 1) {
          turnstileServerRetryCountRef.current += 1
          pendingRegistrationRef.current = true
        } else {
          pendingRegistrationRef.current = false
        }
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleTurnstileTokenChange = (token) => {
    setTurnstileToken(token)
    if (!token || !pendingRegistrationRef.current) return
    pendingRegistrationRef.current = false
    setError('')
    void submitRegistration(token)
  }

  const submit = async (event) => {
    event.preventDefault()
    setError('')

    if (isRegister) {
      if (!form.username.trim()) return setError(t('auth.inputUsername'))
      if (!form.email.trim()) return setError(t('auth.inputEmail'))
      if (!form.password) return setError(t('auth.inputPassword'))
      if (form.password.length < 8) return setError(t('auth.passwordLength'))
      if (form.password !== form.confirmPassword) return setError(t('auth.passwordMismatch'))
      if (import.meta.env.PROD && !turnstileSiteKey) return setError(t('auth.turnstileMissing'))
      if (turnstileSiteKey && !turnstileToken) {
        pendingRegistrationRef.current = true
        turnstileServerRetryCountRef.current = 0
        setTurnstileRequested(true)
        return
      }
      turnstileServerRetryCountRef.current = 0
      return submitRegistration(turnstileToken)
    } else if (!loginChallengePassed) {
      return setError(t('auth.loginHumanCheck'))
    }

    setSubmitting(true)
    try {
      const data = await login({ username: form.username, password: form.password })
      completeLogin(data)
      navigate(location.state?.from || '/growth', { replace: true })
    } catch (requestError) {
      setError(getAuthErrorMessage(requestError, t))
      resetLoginChallenge()
      if (requestError.code === 'EMAIL_NOT_VERIFIED') setUnverifiedEmail(requestError.details?.email || '')
    } finally {
      setSubmitting(false)
    }
  }

  const resendVerification = async (email) => {
    if (!email) return
    setResending(true)
    setResendMessage('')
    setError('')
    try {
      const data = await sendVerificationEmail(email)
      setResendMessage(data.message || t('auth.verificationResent'))
      if (registrationResult) setRegistrationResult((current) => ({ ...current, mailSent: true }))
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setResending(false)
    }
  }

  const loginWithGithub = async () => {
    setError('')
    if (!loginChallengePassed) {
      setError(t('auth.loginHumanCheck'))
      return
    }
    setGithubSubmitting(true)
    try {
      const { authorizationUrl, state } = await startGithubOAuth()
      if (!authorizationUrl) throw new Error(t('auth.githubMissingUrl'))
      saveGithubOAuthState(state)
      window.location.href = authorizationUrl
    } catch (requestError) {
      if (requestError.code === 'GITHUB_OAUTH_NOT_CONFIGURED') {
        setGithubOAuthEnabled(false)
      } else {
        setError(requestError.message || t('auth.githubLoginFailed'))
      }
      setGithubSubmitting(false)
    }
  }

  return (
    <main className={`auth-screen ${isRegister ? 'is-register' : 'is-login'}`}>
      <div className="auth-shell">
        <AuthProductPanel />
        <section className="auth-panel">
          {registrationResult ? (
            <div className="auth-result" role="status">
              <span className="auth-result-icon"><MailCheck size={28} /></span>
              <span className="eyebrow">{t('auth.emailVerification')}</span>
              <h1>{t('auth.verifyTitle')}</h1>
              <p>
                {registrationResult.mailSent ? t('auth.verifySent') : t('auth.verifyCreated')}
                <strong>{registrationResult.email}</strong>
              </p>
              <p className="auth-result-note">{t('auth.verifyNote')}</p>
              {resendMessage && <p className="form-success">{resendMessage}</p>}
              {error && <p className="form-error" role="alert">{error}</p>}
              <button className="secondary-button auth-resend" type="button" disabled={resending} onClick={() => resendVerification(registrationResult.email)}>
                {resending ? <LoaderCircle className="spin" size={17} /> : <RefreshCw size={17} />}
                {resending ? t('authForm.processing') : t('auth.resend')}
              </button>
              <Link className="primary-button auth-result-login" to="/login">{t('auth.emailVerified')}</Link>
            </div>
          ) : (
            <>
              <div className="auth-heading">
                <span className="auth-heading-icon">{isRegister ? <ShieldCheck size={19} /> : <CheckCircle2 size={19} />}</span>
                <h1>{isRegister ? t('authForm.registerHeading') : t('authForm.loginHeading')}</h1>
                <p>{isRegister ? t('authForm.registerIntro') : t('authForm.loginIntro')}</p>
              </div>

              {!isRegister && (
                <>
                  {location.state?.registrationEmail && (
                    <div className="auth-registration-success" role="status">
                      <MailCheck size={19} />
                      <p>
                        {t('auth.registrationSuccessRedirect')}
                        <strong>{location.state.registrationEmail}</strong>
                      </p>
                    </div>
                  )}
                  <button className="github-login-button" type="button" disabled={githubSubmitting || githubOAuthEnabled === false} onClick={loginWithGithub}>
                    <GitBranch size={19} />
                    {githubSubmitting ? t('authForm.githubProcessing') : githubOAuthEnabled === false ? t('authForm.githubUnavailable') : t('authForm.githubLogin')}
                  </button>
                  {githubOAuthEnabled === false && <p className="github-oauth-note">{t('authForm.githubFallback')}</p>}
                  <div className="auth-divider"><span>{t('authForm.divider')}</span></div>
                </>
              )}

              {!isRegister && (
                <section className="auth-human-check" aria-labelledby="login-human-check-title">
                  <div>
                    <span className="auth-human-check-icon"><ShieldCheck size={17} /></span>
                    <div>
                      <h2 id="login-human-check-title">{t('authForm.loginHumanTitle')}</h2>
                      <p>{t('authForm.loginHumanDesc')}</p>
                    </div>
                  </div>
                  <label htmlFor="loginHumanAnswer">{t('authForm.loginHumanQuestion', { question: loginChallenge.label })}</label>
                  <div className="auth-human-check-row">
                    <input
                      id="loginHumanAnswer"
                      inputMode="numeric"
                      name="loginHumanAnswer"
                      placeholder={t('authForm.loginHumanPlaceholder')}
                      value={loginChallengeAnswer}
                      onChange={(event) => setLoginChallengeAnswer(event.target.value)}
                      maxLength={3}
                      autoComplete="off"
                    />
                    <button type="button" onClick={resetLoginChallenge}>
                      <RefreshCw size={15} />
                      {t('authForm.loginHumanRefresh')}
                    </button>
                  </div>
                </section>
              )}

              <form className="auth-form" onSubmit={submit}>
                <label htmlFor="username">{isRegister ? t('authForm.username') : t('authForm.usernameOrEmail')}</label>
                <div className="input-wrap">
                  <User size={18} />
                  <input id="username" name="username" placeholder={isRegister ? t('authForm.usernamePlaceholder') : t('authForm.usernameOrEmailPlaceholder')} value={form.username} onChange={updateField} autoComplete="username" required minLength={isRegister ? 3 : 1} maxLength={190} />
                </div>

                {isRegister && (
                  <>
                    <label htmlFor="email">{t('authForm.email')}</label>
                    <div className="input-wrap">
                      <Mail size={18} />
                      <input id="email" name="email" type="email" placeholder={t('authForm.emailPlaceholder')} value={form.email} onChange={updateField} autoComplete="email" required maxLength={190} />
                    </div>
                  </>
                )}

                <div className="auth-label-row">
                  <label htmlFor="password">{t('authForm.password')}</label>
                  {!isRegister && <Link to="/forgot-password">{t('authForm.forgotPassword')}</Link>}
                </div>
                <div className="input-wrap">
                  <LockKeyhole size={18} />
                  <input id="password" name="password" type="password" placeholder={isRegister ? t('authForm.newPasswordPlaceholder') : t('authForm.passwordPlaceholder')} value={form.password} onChange={updateField} autoComplete={isRegister ? 'new-password' : 'current-password'} required minLength={8} maxLength={72} />
                </div>

                {isRegister && (
                  <>
                    <label htmlFor="confirmPassword">{t('authForm.confirmPassword')}</label>
                    <div className="input-wrap">
                      <LockKeyhole size={18} />
                      <input id="confirmPassword" name="confirmPassword" type="password" placeholder={t('authForm.confirmPasswordPlaceholder')} value={form.confirmPassword} onChange={updateField} autoComplete="new-password" required minLength={8} maxLength={72} />
                    </div>
                    <label>{t('authForm.humanCheck')}</label>
                    {turnstileSiteKey ? (
                      turnstileRequested ? (
                        <TurnstileWidget
                          key={turnstileResetKey}
                          siteKey={turnstileSiteKey}
                          onTokenChange={handleTurnstileTokenChange}
                        />
                      ) : (
                        <p className="turnstile-note">{t('auth.turnstileLazyHint')}</p>
                      )
                    ) : (
                      <p className={`turnstile-note ${import.meta.env.PROD ? 'is-error' : ''}`}>
                        {import.meta.env.PROD ? t('auth.turnstileProdMissing') : t('auth.turnstileDevMissing')}
                      </p>
                    )}
                  </>
                )}

                {error && <p className="form-error" role="alert">{error}</p>}
                {unverifiedEmail && (
                  <div className="auth-verification-help">
                    <p>{t('auth.unverified')}</p>
                    <button type="button" disabled={resending} onClick={() => resendVerification(unverifiedEmail)}>
                      {resending ? t('authForm.processing') : t('auth.resendNow')}
                    </button>
                  </div>
                )}
                {resendMessage && <p className="form-success">{resendMessage}</p>}

                <button
                  className="primary-button auth-submit"
                  type="submit"
                  disabled={submitting || (isRegister && turnstileRequested && !turnstileToken)}
                >
                  {submitting && <LoaderCircle className="spin" size={17} />}
                  {submitting
                    ? t('authForm.processing')
                    : isRegister && turnstileRequested && !turnstileToken
                      ? t('authForm.completeHumanCheck')
                      : isRegister
                        ? t('authForm.submitRegister')
                        : t('authForm.submitLogin')}
                </button>
              </form>

              <p className="auth-switch">
                {isRegister ? t('authForm.hasAccount') : t('authForm.noAccount')}
                <Link to={isRegister ? '/login' : '/register'}>{isRegister ? t('authForm.goLogin') : t('authForm.createAccount')}</Link>
              </p>
            </>
          )}
        </section>
      </div>
    </main>
  )
}

export default AuthPage
